import type { PolymorphicProps } from "@kobalte/core/polymorphic";
import type {
  PopoverContentProps,
  PopoverRootProps,
} from "@kobalte/core/popover";
import {
  Popover as PopoverPrimitive,
  usePopoverContext,
} from "@kobalte/core/popover";
import type { ParentProps, ValidComponent } from "solid-js";
import { mergeProps, Show, splitProps, untrack } from "solid-js";
import { cn } from "@/libs/cn";
import { createFocusReturn, FocusGuard } from "./layer-focus";

export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverTitle = PopoverPrimitive.Title;
export const PopoverDescription = PopoverPrimitive.Description;

export const Popover = (props: PopoverRootProps) => {
  const merge = mergeProps<PopoverRootProps[]>(
    {
      gutter: 4,
      flip: false,
    },
    props,
  );

  return <PopoverPrimitive {...merge} />;
};

type popoverContentProps<T extends ValidComponent = "div"> = ParentProps<
  PopoverContentProps<T> & {
    class?: string;
    hideCloseButton?: boolean;
    /**
     * Keeps the popover readable inside a modal dialog. The popover is
     * portalled to `body`, and the dialog marks `aria-hidden` every node it
     * does not contain, so screen readers, and role queries, find nothing in
     * it. This sets Kobalte's own top-layer marker, as `SelectContent` does:
     * the hide-outside walk skips it, and focus traps and outside-click checks
     * count it as inside.
     *
     * Every layer reads that marker, not only the dialog, so it is opt-in and
     * only for a leaf popover: one that neither contains another layer (a
     * select, a menu, a date picker) nor opens one. A select open inside a
     * marked popover would stop closing on a click elsewhere in the popover.
     */
    topLayer?: boolean;
  }
>;

/**
 * The frame caps at the space the popper has left in the viewport and the body
 * scrolls inside it. This one flips neither way — `Popover` sets `flip: false`
 * above — so a tall popover on a short viewport has nothing but the cap keeping
 * it on screen.
 *
 * The cap sits on the frame and not on the body because the frame is the padded
 * box: `max-height` bounds the border box, so capping the body instead would
 * let the padding push the popover past the fold. Keeping the body a separate
 * element is also what keeps the close button — positioned against the frame —
 * pinned instead of scrolling away with the content.
 *
 * The frame is also the element that receives `local.class`, so a call site
 * passing its own `max-h-*` replaces the cap through `twMerge` instead of
 * composing with it — none does today.
 *
 * The popover is not modal, so it traps no focus, and being portalled to the
 * end of `body` it is not where Tab expects it: two `FocusGuard`s put it back
 * in page order, right after its trigger. A popover with only an anchor
 * (`ResponsivePopover`) has no trigger for that, nor for Kobalte to return
 * focus to on close, so it uses whatever opened it for both.
 */
export const PopoverContent = <T extends ValidComponent = "div">(
  props: PolymorphicProps<T, popoverContentProps<T>>,
) => {
  const [local, rest] = splitProps(props as popoverContentProps, [
    "class",
    "children",
    "hideCloseButton",
    "topLayer",
    "onOpenAutoFocus",
    "onCloseAutoFocus",
  ]);
  const context = usePopoverContext();
  const focusReturn = createFocusReturn();
  const reference = () => context.triggerRef() ?? focusReturn.invoker();

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        class={cn(
          "z-50 flex max-h-(--kb-popper-content-available-height) w-72 flex-col rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden data-expanded:animate-in data-closed:animate-out data-closed:fade-out-0 data-expanded:fade-in-0 data-closed:zoom-out-95 data-expanded:zoom-in-95 origin-(--kb-popover-content-transform-origin)",
          local.class,
        )}
        data-kb-top-layer={local.topLayer ? "" : undefined}
        onOpenAutoFocus={(event) =>
          untrack(() => {
            focusReturn.remember();
            local.onOpenAutoFocus?.(event);
          })
        }
        onCloseAutoFocus={(event) => {
          local.onCloseAutoFocus?.(event);
          // With a trigger, Kobalte decides: it skips it after a click outside.
          if (!context.triggerRef()) focusReturn.restore(event);
        }}
        {...rest}
      >
        <FocusGuard edge="start" reference={reference} />
        <div class="min-h-0 overflow-y-auto">{local.children}</div>
        <Show when={!local.hideCloseButton}>
          <PopoverPrimitive.CloseButton class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-[opacity,box-shadow] hover:opacity-100 focus:outline-hidden focus:ring-[1.5px] focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              class="h-4 w-4"
            >
              <title>Close</title>
              <path
                fill="none"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M18 6L6 18M6 6l12 12"
              />
            </svg>
          </PopoverPrimitive.CloseButton>
        </Show>
        <FocusGuard edge="end" reference={reference} />
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
};
